import { Check, ChevronsUpDown, X } from "lucide-react";
import * as React from "react";

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { cn } from "../../lib/utils";

export type ComboboxOption = {
  value: string;
  label: string;
};

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  label?: string;
  disabled?: boolean;
}

export function Combobox({
  options,
  value,
  onChange,
  emptyMessage,
  className,
  label,
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const id = React.useId();
  const { t } = useI18n();

  const selectedOption = options?.find((option) => option.value === value);
  const text = React.useMemo(() => {
    if (selectedOption) {
      return selectedOption.label;
    }
    if (open && !selectedOption) {
      return t("generic.selectAnOption");
    }
    return "";
  }, [selectedOption, open, t]);

  const emptyText = React.useMemo(() => {
    return emptyMessage || t("generic.noResultsFound");
  }, [emptyMessage, t]);

  return (
    <div className={cn("relative", className)}>
      {label && (
        <span
          className={cn(
            "pointer-events-none absolute left-[4px] top-2 z-10 origin-[0] scale-100 text-sm text-primary transition-all duration-200 bg-background px-2",
            (open || selectedOption) && "-top-[10px] scale-90"
          )}
        >
          {label}
        </span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "peer h-10 w-full justify-between hover:bg-background rounded-md border-input",
              !value && "text-muted-foreground",
              open && "ring-1 ring-ring ring-offset-2"
            )}
            disabled={disabled}
            onClick={(e) => {
              // Não abre se o clique foi no X
              if ((e.target as HTMLElement).closest('[data-clear-button]')) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}
          >
            <div className="flex-1 -ml-1 text-left truncate text-foreground text-sm">
              {text}
            </div>
            <div className="flex">
              {value && (
                <div
                  data-clear-button
                  className="mr-1 flex items-center justify-center"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(undefined);
                    setOpen(false);
                  }}
                >
                  <X className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 cursor-pointer" />
                </div>
              )}
              <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0 rounded-md border border-muted/55 pointer-events-auto"
        >
          <Command className="bg-background">
            <CommandInput
              placeholder={t("generic.search")}
              value={searchQuery}
              onValueChange={setSearchQuery}
              onBlur={() => {
                setSearchQuery("");
              }}
            />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto">
                {options?.length > 0 ? (
                  options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      title={option.label}
                      onSelect={() => {
                        onChange(option.value);
                        setSearchQuery("");
                        setOpen(false);
                      }}
                      className="cursor-pointer hover:bg-accent aria-selected:bg-accent rounded-md"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))
                ) : (
                  <CommandItem key="no-results">
                    {t("generic.noResultsFound")}
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
